import React, { useState, useEffect } from "react";
import api from "../api";
import { CheckCircleIcon, ArrowPathIcon, ClipboardDocumentIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";

export default function AdminSwaps() {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState(null);

  useEffect(() => {
    loadPendingSwaps();
  }, []);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copiedAddress) {
      const timer = setTimeout(() => {
        setCopiedAddress(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedAddress]);

  const loadPendingSwaps = async () => {
    setLoading(true);
    
    try {
      const { data } = await api.get("/admin/pending-swaps");
      setSwaps(data.swaps || []);
      setError("");
    } catch (err) {
      console.error("Failed to load swaps:", err);
      setError("Failed to load pending swaps");
    } finally {
      setLoading(false);
    }
  };

  const completeSwap = async (swapId) => {
    setProcessingId(swapId);
    
    try {
      await api.post("/admin/complete-swap", { swapId });
      
      // Remove from list
      setSwaps(swaps.filter(swap => swap._id !== swapId));
    } catch (err) {
      console.error("Failed to complete swap:", err);
      setError("Failed to mark swap as complete");
    } finally {
      setProcessingId(null);
    }
  };

  const copyToClipboard = (address, id) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        setCopiedAddress(id);
      })
      .catch(err => {
        console.error("Failed to copy:", err);
        setError("Failed to copy wallet address");
      });
  };

  // Format date to be more compact
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  if (loading && swaps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-gray-400">
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />
          <span>Loading pending swaps...</span>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Pending Swaps</h1>
          <button 
            onClick={loadPendingSwaps}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-md text-sm flex items-center transition-colors duration-200 shadow-lg"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center text-red-200 text-sm shadow-md">
            <ExclamationCircleIcon className="h-5 w-5 mr-2 text-red-400" />
            {error}
          </div>
        )}
        
        <div className="mb-4 flex justify-between items-center">
          <div className="text-sm text-gray-300">
            {swaps.length} pending {swaps.length === 1 ? 'swap' : 'swaps'}
          </div>
        </div>

        {swaps.length === 0 ? (
          <div className="p-6 text-center bg-gray-800/50 rounded-lg border border-gray-700 text-gray-400 shadow-md">
            No pending swaps to process
          </div>
        ) : (
          <div>
            {/* Mobile view - Card layout (visible on all devices) */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {swaps.map((swap) => (
                <div key={swap._id} className="p-4 rounded-lg border border-gray-700 bg-gray-800/50 shadow-lg transition-all duration-200 hover:border-gray-500">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm font-medium truncate max-w-[70%]">{swap.userEmail}</div>
                    <div className="text-xs text-gray-400 bg-gray-700/50 px-2 py-1 rounded-full">
                      {formatDate(swap.createdAt)}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-700/30 p-3 rounded-lg">
                      <div className="text-xs text-gray-400 mb-1">Chips</div>
                      <div className="font-medium text-blue-300">{swap.chipAmount.toLocaleString()}</div>
                    </div>
                    <div className="bg-gray-700/30 p-3 rounded-lg">
                      <div className="text-xs text-gray-400 mb-1">USDT</div>
                      <div className="font-medium text-green-300">
                        {(parseFloat(swap.usdtAmount || (swap.chipAmount * 0.001)) * 0.9).toFixed(3)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-xs text-gray-400 mb-1">Wallet Address</div>
                    <div className="flex items-center bg-gray-900/80 p-2 rounded-md">
                      <div className="font-mono text-xs overflow-x-auto flex-grow break-all mr-2">
                        {swap.walletAddress}
                      </div>
                      <button 
                        onClick={() => copyToClipboard(swap.walletAddress, swap._id)}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 flex-shrink-0 transition-colors duration-200"
                        title="Copy to clipboard"
                      >
                        {copiedAddress === swap._id ? (
                          <span className="text-xs text-green-400 px-1">Copied</span>
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => completeSwap(swap._id)}
                    disabled={processingId === swap._id}
                    className={`w-full py-2 rounded-md flex items-center justify-center text-sm font-medium transition-all duration-200 shadow-md ${
                      processingId === swap._id
                        ? "bg-gray-700 text-gray-400"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    {processingId === swap._id ? (
                      <div className="flex items-center">
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span>Mark Done</span>
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
